/**
 * Cross-Container Drag and Drop Manager
 * 
 * Handles drag-and-drop of sections across different containers
 * (left panel, right panel, tabs-below-canvas)
 */

import { settings } from './Settings.js';

export class CrossContainerDragDrop {
    constructor(apiBase = '/api/user/') {
        this.apiBase = apiBase;
        this.draggedElement = null;
        this.dragOverContainer = null;
        this.isLoggedIn = false;
        this.userId = null;
        this.containers = [
            { selector: '#left-panel-sections', id: 'left-panel' },
            { selector: '#right-panel-sections', id: 'right-panel' },
            { selector: '.tabs-below-canvas', id: 'tabs-below-canvas' },
            { selector: '#resources-panel', id: 'resources-panel' },
            { selector: '#military-panel', id: 'military-panel' }
        ];
        
        // Canvas area drop zone - drops go to tabs-below-canvas
        this.canvasDropZone = '.canvas-layers, .canvas-area';
        
        // Drag and drop enabled state (default to false, load from settings)
        this.enabled = settings.get('dragDropEnabled') || false;
        
        // Store event handlers for cleanup
        this.containerHandlers = new Map();
        this.canvasHandlers = new Map();
        this.sectionHandlers = new Map();
        
        this.init();
    }
    
    async init() {
        // Check if user is logged in
        await this.checkLoginStatus();
        
        // Setup cross-container drag and drop (only if enabled)
        if (this.enabled) {
            this.setupCrossContainerDragDrop();
        }
        
        // Load saved section positions
        await this.loadSectionPositions();
        
        // Re-setup after a delay to catch dynamically added sections (like ResourcePanel)
        setTimeout(() => {
            if (this.enabled) {
                this.refreshDragAndDrop();
            }
        }, 500);
    }
    
    /**
     * Refresh drag and drop setup for newly added sections
     */
    refreshDragAndDrop() {
        // Get all sections that haven't been set up yet
        const allSections = document.querySelectorAll('.collapsible-section:not(.cross-container-draggable), .optimization-section:not(.cross-container-draggable)');
        
        allSections.forEach(section => {
            this.setupSectionDragDrop(section);
        });
    }
    
    /**
     * Setup drag and drop for a single section
     */
    setupSectionDragDrop(section) {
        // Skip if not enabled
        if (!this.enabled) {
            return;
        }
        
        // Skip if already set up
        if (section.classList.contains('cross-container-draggable')) {
            return;
        }
        
        // Ensure section has an ID
        if (!section.dataset.sectionId) {
            const header = section.querySelector('h3, h4');
            if (header) {
                section.dataset.sectionId = header.textContent.trim().toLowerCase().replace(/\s+/g, '-');
            }
        }
        
        // Make section draggable
        section.draggable = true;
        section.classList.add('draggable-section', 'cross-container-draggable');
        
        // Add drag handle if not present
        if (!section.querySelector('.drag-handle')) {
            const dragHandle = document.createElement('div');
            dragHandle.className = 'drag-handle';
            dragHandle.innerHTML = '⋮⋮';
            dragHandle.title = 'Drag to reorder or move to another panel';
            dragHandle.style.cssText = 'cursor: move; color: var(--text-secondary, #999); font-size: 14px; padding: 4px; user-select: none;';
            section.insertBefore(dragHandle, section.firstChild);
        }
        
        // Create event handlers
        const dragStartHandler = (e) => {
            if (!this.enabled) {
                e.preventDefault();
                return;
            }
            
            // Stop propagation to prevent panel drag from triggering
            e.stopPropagation();
            
            this.draggedElement = section;
            section.classList.add('dragging');
            e.dataTransfer.effectAllowed = 'move';
            e.dataTransfer.setData('text/plain', section.dataset.sectionId || '');
            
            // Mark that a section is being dragged (to prevent panel drag)
            e.dataTransfer.setData('is-section-drag', 'true');
            e.dataTransfer.setData('section-id', section.dataset.sectionId || '');
            
            // Store element reference for trash
            e.dataTransfer.setData('dragged-element-id', section.dataset.sectionId || section.id || '');
        };
        
        const dragEndHandler = () => {
            section.classList.remove('dragging');
            // Clear all drag-over states
            document.querySelectorAll('.drag-over-container').forEach(el => {
                el.classList.remove('drag-over-container');
            });
            this.draggedElement = null;
            this.dragOverContainer = null;
        };
        
        // Store handlers for cleanup
        this.sectionHandlers.set(section, { dragstart: dragStartHandler, dragend: dragEndHandler });
        
        // Add event listeners
        section.addEventListener('dragstart', dragStartHandler);
        section.addEventListener('dragend', dragEndHandler);
    }
    
    /**
     * Check if user is logged in
     */
    async checkLoginStatus() {
        try {
            const response = await fetch(`${this.apiBase.replace('/user/', '/auth/')}check.php`);
            if (response.ok) {
                const contentType = response.headers.get('content-type');
                if (contentType && contentType.includes('application/json')) {
                    const data = await response.json();
                    this.isLoggedIn = data.logged_in || false;
                    this.userId = data.user_id || null;
                } else {
                    // Response is not JSON (likely HTML 404 page) - old PHP endpoint doesn't exist
                    this.isLoggedIn = false;
                }
            }
        } catch (e) {
            // Silently fail - auth check is optional for planner mode
            this.isLoggedIn = false;
        }
    }
    
    /**
     * Setup cross-container drag and drop
     */
    setupCrossContainerDragDrop() {
        // Get all collapsible sections and optimization sections across all containers
        const allSections = document.querySelectorAll('.collapsible-section, .optimization-section');
        
        allSections.forEach(section => {
            this.setupSectionDragDrop(section);
        });
        
        // Setup drop zones for all containers
        this.containers.forEach(containerInfo => {
            const container = document.querySelector(containerInfo.selector);
            if (!container) return;
            
            // Create event handlers
            const dragoverHandler = (e) => {
                if (!this.enabled) {
                    return;
                }
                e.preventDefault();
                e.dataTransfer.dropEffect = 'move';
                
                if (this.draggedElement && !container.contains(this.draggedElement)) {
                    // Clear other containers
                    document.querySelectorAll('.drag-over-container').forEach(el => {
                        el.classList.remove('drag-over-container');
                    });
                    container.classList.add('drag-over-container');
                    this.dragOverContainer = container;
                } else if (this.draggedElement && container.contains(this.draggedElement)) {
                    // Same container - just provide visual feedback, don't move during dragover
                    // The actual move will happen in the drop event
                    container.classList.add('drag-over-container');
                }
            };
            
            const dragleaveHandler = (e) => {
                if (!this.enabled) {
                    return;
                }
                // Only remove if we're actually leaving the container
                if (!container.contains(e.relatedTarget)) {
                    container.classList.remove('drag-over-container');
                }
            };
            
            const dropHandler = (e) => {
                if (!this.enabled) {
                    return;
                }
                e.preventDefault();
                container.classList.remove('drag-over-container');
                
                if (this.draggedElement) {
                    // Validate that dragged element is not a parent of the container
                    if (this.draggedElement.contains(container)) {
                        console.warn('Cannot move element: would create circular reference');
                        return;
                    }
                    
                    // If element is already in this container, remove it first to avoid issues
                    if (container.contains(this.draggedElement)) {
                        // Temporarily remove to recalculate position
                        const parent = this.draggedElement.parentNode;
                        if (parent) {
                            parent.removeChild(this.draggedElement);
                        }
                    }
                    
                    // Find insertion point
                    const afterElement = this.getDragAfterElement(container, e.clientY);
                    
                    // Validate that afterElement is actually a child of container
                    if (afterElement != null && !container.contains(afterElement)) {
                        // Invalid reference, append to end instead
                        container.appendChild(this.draggedElement);
                    } else if (afterElement == null) {
                        container.appendChild(this.draggedElement);
                    } else {
                        // Validate that we're not trying to insert before the dragged element itself
                        if (afterElement === this.draggedElement) {
                            container.appendChild(this.draggedElement);
                        } else {
                            container.insertBefore(this.draggedElement, afterElement);
                        }
                    }
                    
                    // Save new positions
                    this.saveSectionPositions();
                }
            };
            
            // Store handlers for cleanup
            this.containerHandlers.set(container, {
                dragover: dragoverHandler,
                dragleave: dragleaveHandler,
                drop: dropHandler
            });
            
            // Add event listeners
            container.addEventListener('dragover', dragoverHandler);
            container.addEventListener('dragleave', dragleaveHandler);
            container.addEventListener('drop', dropHandler);
        });
        
        // Setup canvas area as drop zone (drops go to tabs-below-canvas)
        this.setupCanvasDropZone();
    }
    
    /**
     * Setup canvas area as a drop zone (redirects drops to tabs-below-canvas)
     */
    setupCanvasDropZone() {
        const canvasArea = document.querySelector(this.canvasDropZone);
        if (!canvasArea) return;
        
        // Create event handlers
        const dragoverHandler = (e) => {
            if (!this.enabled) {
                return;
            }
            e.preventDefault();
            e.dataTransfer.dropEffect = 'move';
            if (this.draggedElement) {
                // Highlight the tabs-below-canvas as the target
                const targetContainer = document.querySelector('.tabs-below-canvas');
                if (targetContainer && !targetContainer.classList.contains('drag-over-container')) {
                    document.querySelectorAll('.drag-over-container').forEach(el => el.classList.remove('drag-over-container'));
                    targetContainer.classList.add('drag-over-container');
                    this.dragOverContainer = targetContainer;
                }
            }
        };
        
        const dragleaveHandler = (e) => {
            if (!this.enabled) {
                return;
            }
            const targetContainer = document.querySelector('.tabs-below-canvas');
            if (targetContainer && !canvasArea.contains(e.relatedTarget)) {
                targetContainer.classList.remove('drag-over-container');
            }
        };
        
        const dropHandler = (e) => {
            if (!this.enabled) {
                return;
            }
            e.preventDefault();
            const targetContainer = document.querySelector('.tabs-below-canvas');
            if (targetContainer) {
                targetContainer.classList.remove('drag-over-container');
            }
            
            if (this.draggedElement) {
                // Move the dragged element to the tabs-below-canvas
                if (targetContainer && !targetContainer.contains(this.draggedElement)) {
                    // Validate that dragged element is not a parent of the container
                    if (this.draggedElement.contains(targetContainer)) {
                        console.warn('Cannot move element: would create circular reference');
                        return;
                    }
                    
                    const afterElement = this.getDragAfterElement(targetContainer, e.clientY);
                    
                    // Validate that afterElement is actually a child of targetContainer
                    if (afterElement != null && !targetContainer.contains(afterElement)) {
                        // Invalid reference, append to end instead
                        targetContainer.appendChild(this.draggedElement);
                    } else if (afterElement == null) {
                        targetContainer.appendChild(this.draggedElement);
                    } else {
                        // Validate that we're not trying to insert before the dragged element itself
                        if (afterElement === this.draggedElement) {
                            targetContainer.appendChild(this.draggedElement);
                        } else {
                            targetContainer.insertBefore(this.draggedElement, afterElement);
                        }
                    }
                    this.saveSectionPositions();
                }
            }
        };
        
        // Store handlers for cleanup
        this.canvasHandlers.set(canvasArea, {
            dragover: dragoverHandler,
            dragleave: dragleaveHandler,
            drop: dropHandler
        });
        
        // Add event listeners
        canvasArea.addEventListener('dragover', dragoverHandler);
        canvasArea.addEventListener('dragleave', dragleaveHandler);
        canvasArea.addEventListener('drop', dropHandler);
    }
    
    /**
     * Get element after which to insert dragged element
     */
    getDragAfterElement(container, y) {
        const draggableElements = [...container.querySelectorAll('.collapsible-section:not(.dragging), .optimization-section:not(.dragging)')];
        
        return draggableElements.reduce((closest, child) => {
            const box = child.getBoundingClientRect();
            const offset = y - box.top - box.height / 2;
            
            if (offset < 0 && offset > closest.offset) {
                return { offset: offset, element: child };
            } else {
                return closest;
            }
        }, { offset: Number.NEGATIVE_INFINITY }).element;
    }
    
    /**
     * Load saved section positions
     */
    async loadSectionPositions() {
        let savedPositions = null;
        
        // Try to load from database first (if logged in)
        if (this.isLoggedIn) {
            try {
                const response = await fetch(`${this.apiBase}preferences.php`);
                if (response.ok) {
                    const data = await response.json();
                    if (data.success && data.preferences && data.preferences.sectionPositions) {
                        savedPositions = data.preferences.sectionPositions;
                        // Sync to localStorage for offline access
                        settings.set('sectionPositions', savedPositions);
                    }
                }
            } catch (e) {
                console.log('Failed to load preferences from database:', e);
            }
        }
        
        // Fallback to localStorage
        if (!savedPositions) {
            const stored = settings.get('sectionPositions');
            if (stored && typeof stored === 'object') {
                savedPositions = stored;
            }
        }
        
        // Apply saved positions if available
        if (savedPositions) {
            this.applySectionPositions(savedPositions);
        }
    }
    
    /**
     * Apply saved section positions
     */
    applySectionPositions(positions) {
        Object.keys(positions).forEach(sectionId => {
            const containerId = positions[sectionId];
            const section = document.querySelector(`[data-section-id="${sectionId}"]`);
            const container = this.containers.find(c => c.id === containerId);
            
            if (section && container) {
                const targetContainer = document.querySelector(container.selector);
                if (targetContainer && !targetContainer.contains(section)) {
                    targetContainer.appendChild(section);
                }
            }
        });
    }
    
    /**
     * Get current section positions
     */
    getCurrentSectionPositions() {
        const positions = {};
        
        this.containers.forEach(containerInfo => {
            const container = document.querySelector(containerInfo.selector);
            if (container) {
                const sections = container.querySelectorAll('.collapsible-section, .optimization-section');
                sections.forEach(section => {
                    const sectionId = section.dataset.sectionId;
                    if (sectionId) {
                        positions[sectionId] = containerInfo.id;
                    }
                });
            }
        });
        
        return positions;
    }
    
    /**
     * Save section positions to database and localStorage
     */
    async saveSectionPositions() {
        const positions = this.getCurrentSectionPositions();
        
        // Save to localStorage immediately
        settings.set('sectionPositions', positions);
        
        // Save to database if logged in
        if (this.isLoggedIn) {
            try {
                // Load existing preferences first to merge
                let existingPrefs = {};
                try {
                    const getResponse = await fetch(`${this.apiBase}preferences.php`);
                    if (getResponse.ok) {
                        const getData = await getResponse.json();
                        if (getData.success && getData.preferences) {
                            existingPrefs = getData.preferences;
                        }
                    }
                } catch (e) {
                    // Ignore errors loading existing prefs
                }
                
                // Merge with new positions
                const preferences = {
                    ...existingPrefs,
                    sectionPositions: positions
                };
                
                const response = await fetch(`${this.apiBase}preferences.php`, {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json'
                    },
                    body: JSON.stringify({ preferences })
                });
                
                if (!response.ok) {
                    console.warn('Failed to save preferences to database');
                }
            } catch (e) {
                console.warn('Error saving preferences to database:', e);
            }
        }
    }
    
    /**
     * Enable drag and drop
     */
    enable() {
        if (this.enabled) {
            return;
        }
        
        this.enabled = true;
        settings.set('dragDropEnabled', true);
        
        // Setup drag and drop
        this.setupCrossContainerDragDrop();
        
        // Refresh for any new sections
        setTimeout(() => {
            this.refreshDragAndDrop();
        }, 100);
    }
    
    /**
     * Disable drag and drop
     */
    disable() {
        if (!this.enabled) {
            return;
        }
        
        this.enabled = false;
        settings.set('dragDropEnabled', false);
        
        // Remove drag handles and make sections non-draggable
        document.querySelectorAll('.cross-container-draggable').forEach(section => {
            section.draggable = false;
            section.classList.remove('draggable-section', 'cross-container-draggable');
            
            // Remove drag handle
            const dragHandle = section.querySelector('.drag-handle');
            if (dragHandle) {
                dragHandle.remove();
            }
            
            // Remove event listeners
            const handlers = this.sectionHandlers.get(section);
            if (handlers) {
                section.removeEventListener('dragstart', handlers.dragstart);
                section.removeEventListener('dragend', handlers.dragend);
                this.sectionHandlers.delete(section);
            }
        });
        
        // Remove container event listeners
        this.containerHandlers.forEach((handlers, container) => {
            container.removeEventListener('dragover', handlers.dragover);
            container.removeEventListener('dragleave', handlers.dragleave);
            container.removeEventListener('drop', handlers.drop);
        });
        this.containerHandlers.clear();
        
        // Remove canvas event listeners
        this.canvasHandlers.forEach((handlers, canvasArea) => {
            canvasArea.removeEventListener('dragover', handlers.dragover);
            canvasArea.removeEventListener('dragleave', handlers.dragleave);
            canvasArea.removeEventListener('drop', handlers.drop);
        });
        this.canvasHandlers.clear();
        
        // Clear drag-over states
        document.querySelectorAll('.drag-over-container').forEach(el => {
            el.classList.remove('drag-over-container');
        });
        
        this.draggedElement = null;
        this.dragOverContainer = null;
    }
    
    /**
     * Toggle drag and drop enabled state
     */
    toggle() {
        if (this.enabled) {
            this.disable();
        } else {
            this.enable();
        }
        return this.enabled;
    }
    
    /**
     * Check if drag and drop is enabled
     */
    isEnabled() {
        return this.enabled;
    }
}

